import { BooksSubscriptionContent } from "pages/Books/BooksDashboard/BooksSubscriptionModalContent";
import Modal from "./DesignSystem/Modal/Modal";
import { useGetSubscriptionsQuery } from "store/apis/subscriptions";
import { BOOKS_PLUS_PLAN } from "constants/subscriptionType";
import { ReactNode, useEffect, useState } from "react";
import { Subscription } from "types/Models/subscription";
import Async from "./DesignSystem/AsyncComponents/Async";
import { useDispatch } from "react-redux";
import { setAddonData, setAddonType } from "store/slices/addons";
import { _MANAGE } from "constants/addons";
import { CheckoutModal } from "./CheckoutModal/CheckoutModal";
import { useModal } from "hooks/useModal";
import { useAddProductToCartMutation } from "store/apis/products";
import { useToast } from "hooks/useToast";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { BackendError } from "types/utils/error";
import dayjs from "dayjs";
import { SmallInfo } from "./icons/SmallInfo";
import { MONTHLY } from "constants/billingCycles";
import { useRoleBasedView } from "hooks/useRoleBasedView";

export const TrialPeriodInfo = ({
  trialEndDate,
}: {
  trialEndDate?: string | null;
}) => {
  const endDate = trialEndDate
    ? dayjs(trialEndDate).format("MMM DD, YYYY")
    : dayjs().add(1, "month").format("MMM DD, YYYY");

  return (
    <div className="t-flex t-flex-col t-gap-1.5">
      <div className="t-flex t-items-center t-gap-1.5 t-text-subtitle-sm t-text-dark_green-70">
        <span>📣</span> First month free!
      </div>
      <div className="t-text-body-sm t-text-text-30 t-italic">
        Start today → Trial ends {endDate} → Plan will auto-renew until
        cancelled after the trial.
      </div>
    </div>
  );
};

export type BooksSoftwareSubscriptionsModalProps = {
  open: boolean;
  onClose: () => void;
  entityId?: string;
  onProductSubscribe?: () => void;
  modalDescription?: ReactNode;
  isAddToCartFlow?: boolean;
};

export const BooksSoftwareSubscriptionsModal = ({
  open,
  onClose,
  entityId,
  onProductSubscribe,
  modalDescription,
  isAddToCartFlow,
}: BooksSoftwareSubscriptionsModalProps) => {
  const {
    data: subscriptionPlans,
    isLoading,
    isSuccess,
  } = useGetSubscriptionsQuery({
    subscription_types: BOOKS_PLUS_PLAN,
  });
  const dispatch = useDispatch();
  const checkoutModal = useModal();
  const [addProductToCart, { isLoading: addingProductToCart }] =
    useAddProductToCartMutation();
  const { successToast, alertToast } = useToast();
  const { uuid: groupId } = useCurrentGroupContext();
  const { isForeignCA } = useRoleBasedView();

  const softwareStandardActivePlan = subscriptionPlans?.find(
    (item) =>
      item?.subscription_type === BOOKS_PLUS_PLAN &&
      item?.group_subscription?.length
  );
  const [selectedPlan, setSelectedPlan] = useState<Subscription | undefined>();

  const softwareStandardBillingCycle =
    selectedPlan?.billing_cycle! === "MONTHLY" ? "month" : "year";

  const softwareStandardAmount = selectedPlan?.amount! || 30;

  const softwareSubscribeStandardButtonText = softwareStandardActivePlan
    ? "Manage Subscription"
    : "Upgrade to Standard";

  const softwareStandardButtonText = isAddToCartFlow
    ? "Add to cart"
    : softwareSubscribeStandardButtonText;

  const softwareStandardButtonType = softwareStandardActivePlan
    ? "secondary"
    : "primary_outlined";
  const onManage = () => {
    dispatch(setAddonData(selectedPlan));
    dispatch(setAddonType(selectedPlan?.subscription_type + _MANAGE));
  };

  const onSubscribed = () => {
    onProductSubscribe?.();
    checkoutModal.close();
  };

  const isTrialPeriodAllowed = Boolean(selectedPlan?.is_trail_period_allowed);

  const addBooksToCart = async () => {
    try {
      if (selectedPlan && entityId && groupId) {
        const { product_content_type_id, uuid } = selectedPlan;

        const payload = {
          product_content_type_id,
          product_id: uuid,
          quantity: 1,
        };

        await addProductToCart({
          groupId: groupId,
          entityId,
          payload: payload,
        }).unwrap();
        successToast({ title: "Item added!" });
        onClose();
      }
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }
  };

  useEffect(() => {
    subscriptionPlans?.at(0) && setSelectedPlan(subscriptionPlans?.at(0));
  }, [subscriptionPlans]);
  return (
    <Modal.Root open={open} onOpenChange={onClose}>
      <Modal.Content size="large">
        <Modal.Header>
          <Modal.Title>Upgrade Your Plan</Modal.Title>
          <Modal.Close />
        </Modal.Header>
        <Modal.Body>
          <Async.Root
            isEmpty={
              !subscriptionPlans ||
              subscriptionPlans.length === 0 ||
              selectedPlan?.metadata?.comparison?.length === 0
            }
            isLoading={isLoading}
            isSuccess={isSuccess}
          >
            <Async.Success>
              <section>
                {modalDescription && <div>{modalDescription}</div>}
                <BooksSubscriptionContent
                  price={softwareStandardAmount}
                  softwareStandardActivePlan={softwareStandardActivePlan}
                  selectedPlan={selectedPlan}
                  billingCycle={softwareStandardBillingCycle}
                  buttonType={softwareStandardButtonType}
                  buttonText={softwareStandardButtonText}
                  onManage={onManage}
                  subscriptionPlans={subscriptionPlans!}
                  setSelectedPlan={setSelectedPlan}
                  checkoutModal={checkoutModal}
                  toggleValue={selectedPlan?.uuid}
                  isAddToCartFlow={isAddToCartFlow}
                  handleAddToCart={addBooksToCart}
                  isLoading={addingProductToCart}
                  plusPlanExtra={
                    isTrialPeriodAllowed &&
                    !softwareStandardActivePlan &&
                    !isAddToCartFlow ? (
                      <TrialPeriodInfo
                        trialEndDate={selectedPlan?.trail_period_end_date}
                      />
                    ) : null
                  }
                />
                <CheckoutModal
                  entityId={entityId}
                  type="subscription"
                  onClose={checkoutModal.close}
                  open={checkoutModal.isOpen}
                  subscription={selectedPlan!}
                  onSubscribed={onSubscribed}
                  payerUserType={isForeignCA && "FCA"}
                />
              </section>
            </Async.Success>
          </Async.Root>
        </Modal.Body>
      </Modal.Content>
    </Modal.Root>
  );
};
